/**
 * Turning a parsed iTunes library into Fadila playlists.
 *
 * Everything here is pure: it takes the parsed library, the match index and
 * the user's choices, and hands back the playlist array to persist. The
 * component does the picking and the rendering, `libraryService` does the
 * writing, and the decisions worth testing live in here.
 *
 * See docs/ITUNES-IMPORT.md.
 */

import Playlist from '../../models/playlist/playlist';
import PlaylistItem from '../../models/playlistItem/playlistItem';
import Track from '../../models/track';
import Constants from '../../settings';
import { ItunesLibrary, ItunesPlaylist, ItunesTrack } from './itunesPlist';
import {
  copyTolerance,
  MatchIndex,
  matchItunesTrack,
  MatchMethod,
  MatchResult,
} from './itunesMatch';

/** An iTunes playlist as the picker shows it. */
export interface PlaylistSummary {
  playlist: ItunesPlaylist;
  /** `2011 / 11.03` - the folders it sits in, then its name. */
  displayName: string;
  /** Everything the playlist references. */
  itemCount: number;
  /** The part of that which is a file track we could hope to match. */
  fileItemCount: number;
}

/**
 * iTunes' own playlists, which are views rather than anything the user made:
 * Library, Music, Podcasts, Movies, Downloaded, Genius and friends.
 *
 * Detected by the `Master` and `Distinguished Kind` flags and never by name,
 * because a real library has hand-made playlists called "Music" and
 * "Podcasts" sitting right next to iTunes' own - in the reference library the
 * user's "Music" has 26,304 tracks and iTunes' has 38,217.
 */
export function isBuiltInPlaylist(playlist: ItunesPlaylist) {
  return playlist.isMaster || playlist.isDistinguished;
}

/** `2011 / 11.03`, walking up `parentId`. */
function displayNameFor(playlist: ItunesPlaylist, byId: Map<string, ItunesPlaylist>) {
  const segments = [playlist.name];

  let parentId = playlist.parentId;
  // A malformed file could point a folder at itself; a depth cap is cheaper
  // than tracking what we have seen.
  let depth = 0;

  while (parentId && depth < 10) {
    const parent = byId.get(parentId);

    if (!parent) {
      break;
    }

    segments.unshift(parent.name);
    parentId = parent.parentId;
    depth += 1;
  }

  return segments.join(' / ');
}

/**
 * The playlists worth offering, in file order.
 *
 * Empty ones and iTunes' own are dropped. Folders are kept: iTunes gives a
 * folder every track of every playlist inside it, so importing one is a
 * legitimate way to ask for "all of 2011" flattened into a single playlist.
 */
export function importablePlaylists(library: ItunesLibrary): PlaylistSummary[] {
  const byId = new Map(library.playlists.map((pl) => [pl.id, pl] as [string, ItunesPlaylist]));

  return library.playlists
    .filter((pl) => !isBuiltInPlaylist(pl) && pl.trackIds.length > 0)
    .map((pl) => {
      let fileItemCount = 0;

      for (const trackId of pl.trackIds) {
        const track = library.tracks.get(trackId);

        if (track && track.isFile) {
          fileItemCount += 1;
        }
      }

      return {
        playlist: pl,
        displayName: displayNameFor(pl, byId),
        itemCount: pl.trackIds.length,
        fileItemCount,
      };
    });
}

export interface PlaylistMatchItem {
  itunesTrack: ItunesTrack;
  result: MatchResult;
}

export interface PlaylistMatchReport {
  summary: PlaylistSummary;
  /** One entry per file track, in iTunes' order. */
  items: PlaylistMatchItem[];
  matched: number;
  /**
   * A candidate was offered but not believed - see `MatchResult`'s
   * `uncertain`. Not part of `matched`, and imported only when the plan asks.
   */
  uncertain: number;
  /**
   * The part of `ambiguous` where the candidates are one recording several
   * times over (`sameRecording`), so any of them is the right music. Counted
   * separately because these can be imported, one copy each, when asked.
   */
  copies: number;
  ambiguous: number;
  unmatched: number;
  /**
   * Referenced by the playlist but absent from the file's own `Tracks` dict.
   * Should be zero; a truncated or hand-edited XML is how it would not be.
   */
  danglingIds: number;
  /** Radio streams and other non-file entries, which have no file to match. */
  streams: number;
  /** Which rule matched how much, for the report and for sanity checks. */
  methodCounts: Partial<Record<MatchMethod, number>>;
}

export function matchPlaylist(
  index: MatchIndex,
  library: ItunesLibrary,
  summary: PlaylistSummary,
): PlaylistMatchReport {
  const items: PlaylistMatchItem[] = [];
  const methodCounts: Partial<Record<MatchMethod, number>> = {};

  let matched = 0;
  let uncertain = 0;
  let copies = 0;
  let ambiguous = 0;
  let unmatched = 0;
  let danglingIds = 0;
  let streams = 0;

  for (const trackId of summary.playlist.trackIds) {
    const itunesTrack = library.tracks.get(trackId);

    if (!itunesTrack) {
      danglingIds += 1;
      continue;
    }

    if (!itunesTrack.isFile) {
      streams += 1;
      continue;
    }

    const result = matchItunesTrack(index, itunesTrack);

    items.push({ itunesTrack, result });

    if (result.kind === 'matched') {
      matched += 1;
      methodCounts[result.method] = (methodCounts[result.method] || 0) + 1;
    } else if (result.kind === 'uncertain') {
      uncertain += 1;
    } else if (result.kind === 'ambiguous') {
      ambiguous += 1;

      if (result.sameRecording) {
        copies += 1;
      }
    } else {
      unmatched += 1;
    }
  }

  return {
    summary,
    items,
    matched,
    uncertain,
    copies,
    ambiguous,
    unmatched,
    danglingIds,
    streams,
    methodCounts,
  };
}

/** Create a new playlist, or add to one that is already there. */
export type ImportMode = 'create' | 'merge';

export interface ImportTarget {
  mode: ImportMode;
  /** The new playlist's name. Only read when creating. */
  name: string;
  /** The playlist being added to. Only read when merging. */
  existingId?: string;
}

export interface ImportPlan {
  report: PlaylistMatchReport;
  target: ImportTarget;
  /**
   * Take the candidate an `uncertain` result offered - the one that is close
   * on length while every alternative is far off.
   *
   * Off by default, because "close on length and nothing else is" is a
   * reason to look, not a reason to believe. A genuine `ambiguous` result is
   * never imported by this or anything else: there, no candidate stands out
   * at all, so there is nothing to opt in to.
   */
  includeUncertain: boolean;
  /**
   * For an ambiguity whose candidates are one recording several times over
   * (`sameRecording`), add the nearest by length rather than none.
   *
   * Off by default like the other flag, but a different kind of choice: no
   * music is being guessed at, only which of several copies of it the
   * playlist points to. Where two iTunes entries land on the same copy the
   * duplicate check adds it once; where each has its own exact hit - a rip
   * and its rerip, say - each gets its own.
   */
  includeCopies: boolean;
}

export interface ImportOutcome {
  plan: ImportPlan;
  /** The playlist as it now stands, created or merged. */
  playlistId: string;
  targetName: string;
  created: boolean;
  /** Items actually added. */
  added: number;
  /**
   * Matches dropped because the track was already in the playlist - either
   * from a previous import, or because two iTunes entries matched the same
   * Fadila track (two "Untitled" cuts off one record, say).
   */
  duplicates: number;
}

export interface AppliedImport {
  /** The whole playlist array to hand to `setPersistPlaylists`. */
  playlists: Playlist[];
  outcomes: ImportOutcome[];
}

/**
 * The tracks a plan wants added, in order: every match, plus the candidate
 * offered by each `uncertain` result if the plan asked for those.
 *
 * An `ambiguous` result contributes nothing, ever. There is no version of
 * this that picks one of a set the length could not separate - that would be
 * a coin flip, and a coin flip dressed as an import decision is exactly what
 * the report exists to avoid.
 */
function tracksToAdd(plan: ImportPlan) {
  const tracks: Track[] = [];

  for (const item of plan.report.items) {
    if (item.result.kind === 'matched') {
      tracks.push(item.result.track);
    } else if (item.result.kind === 'uncertain' && plan.includeUncertain) {
      tracks.push(item.result.track);
    } else if (
      item.result.kind === 'ambiguous' &&
      item.result.sameRecording &&
      plan.includeCopies
    ) {
      // Nearest first, so this is the copy closest in length - which, when
      // the copies differ at all, is the one iTunes actually had.
      tracks.push(item.result.candidates[0]);
    }
  }

  return tracks;
}

/**
 * A name that is free to use.
 *
 * `createPlaylist` refuses a duplicate name and so does this, but silently
 * failing half an import over a name clash would be a poor trade, so a clash
 * gets a numeric suffix. The library's own pseudo-playlist name is taken too.
 */
export function uniquePlaylistName(name: string, existing: Playlist[]) {
  const taken = new Set(existing.map((pl) => pl.name));
  taken.add(Constants.LibraryName);

  if (!taken.has(name)) {
    return name;
  }

  let suffix = 2;

  while (taken.has(name + ' (' + suffix + ')')) {
    suffix += 1;
  }

  return name + ' (' + suffix + ')';
}

/**
 * Applies every plan to the playlist array.
 *
 * One pass producing one new array, because `setPersistPlaylists` writes the
 * lot and a per-playlist call would mean one cloud write each. Merges keep the
 * existing order and append; creations go on the end in plan order.
 */
export function applyImports(existing: Playlist[], plans: ImportPlan[]): AppliedImport {
  let playlists = existing.slice();
  const outcomes: ImportOutcome[] = [];

  for (const plan of plans) {
    const tracks = tracksToAdd(plan);

    const targetIndex =
      plan.target.mode === 'merge'
        ? playlists.findIndex((pl) => pl.id === plan.target.existingId)
        : -1;

    // A merge target that has gone missing is created instead of dropped.
    const merging = targetIndex !== -1;

    const target = merging
      ? playlists[targetIndex]
      : new Playlist(uniquePlaylistName(plan.target.name, playlists));

    const seen = new Set(target.tracks.map((item) => item.trackType + ':' + item.id));

    const additions: PlaylistItem[] = [];
    let duplicates = 0;

    for (const track of tracks) {
      const key = track.type + ':' + track.id;

      if (seen.has(key)) {
        duplicates += 1;
        continue;
      }

      seen.add(key);
      additions.push(PlaylistItem.from(track));
    }

    const updated: Playlist = {
      ...target,
      tracks: target.tracks.concat(additions),
    };

    if (merging) {
      playlists = playlists
        .slice(0, targetIndex)
        .concat(updated)
        .concat(playlists.slice(targetIndex + 1));
    } else {
      playlists = playlists.concat(updated);
    }

    outcomes.push({
      plan,
      playlistId: updated.id,
      targetName: updated.name,
      created: !merging,
      added: additions.length,
      duplicates,
    });
  }

  return { playlists, outcomes };
}

/** "2.5s" - short enough for a table cell, precise enough to judge by. */
export function formatSeconds(milliseconds: number) {
  return (milliseconds / 1000).toFixed(1).replace(/\.0$/, '') + 's';
}

/**
 * A line saying why a result is not a match, for the row and for the export.
 * Empty for anything that needs no explanation.
 */
export function doubtDescription(result: MatchResult) {
  if (result.kind === 'uncertain') {
    return (
      'closest is ' +
      formatSeconds(result.distance) +
      ' out, next is ' +
      formatSeconds(result.runnerUpDistance) +
      ' away'
    );
  }

  if (result.kind === 'ambiguous' && result.sameRecording) {
    return (
      'your library holds this recording ' +
      result.candidates.length +
      ' times, all within ' +
      formatSeconds(copyTolerance) +
      ' of each other'
    );
  }

  if (result.kind === 'ambiguous') {
    return (
      result.candidates.length +
      ' tracks share this artist, album and ' +
      'title and the lengths cannot separate them'
    );
  }

  return '';
}

/**
 * Everything a report did not simply match, as text to paste somewhere.
 *
 * Tab separated. The old path is last because it is the widest column; the
 * reason comes before it, because for an uncertain row the two distances are
 * the whole decision. Which track was offered is named too - an uncertain row
 * is one the user may want to go and check by hand.
 */
export function missesAsText(report: PlaylistMatchReport) {
  const lines = [
    '# ' + report.summary.displayName,
    '# ' +
      report.matched +
      ' matched, ' +
      report.uncertain +
      ' uncertain, ' +
      report.ambiguous +
      ' ambiguous (' +
      report.copies +
      ' of them copies), ' +
      report.unmatched +
      ' not found',
    ['status', 'artist', 'title', 'album', 'reason', 'offered', 'path'].join('\t'),
  ];

  for (const item of report.items) {
    if (item.result.kind === 'matched') {
      continue;
    }

    const track = item.itunesTrack;

    const offered =
      item.result.kind === 'uncertain'
        ? item.result.track.artist +
          ' - ' +
          item.result.track.title +
          ' | ' +
          item.result.track.album +
          ' | ' +
          formatSeconds(item.result.track.length)
        : '';

    lines.push(
      [
        item.result.kind,
        track.artist || '',
        track.name,
        track.album || '',
        doubtDescription(item.result),
        offered,
        track.location || '',
      ].join('\t'),
    );
  }

  return lines.join('\n');
}
